import { Table } from 'legacy/lut/library/list'
import { FC, useMemo, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Switch } from 'core-components/library/checkbox'
import { Input } from 'core-components/library/inputs'
import { IColumn } from 'core-components/library/list/types'
import { Card } from 'partner360/components/card'
import { CockpitCardHeader } from 'partner360/features/financialCockpit/ui/components/cockpitCardHeader/CockpitCardHeader'
import { CockpitIconButton } from 'partner360/features/financialCockpit/ui/components/cockpitIconButton'

import { useGetEspSponsorListQuery, useSaveEspSponsorMutation } from '../../api/espApi'
import { ESP_TRANSLATION_KEYS } from '../../configs/translations'
import { IEspSponsorOption } from '../../model/types'

import { EspSponsorDialog } from './EspSponsorDialog'
import { isEspSponsorActive, toEspSponsorInput } from './espSponsorInput'

import styles from './EspSponsorsCard.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.sponsors

const valueOrDash = (value: string | null) => value || <span className={styles.none}>-</span>

/**
 * The sponsor directory (form 2111) for the program team: search, add, edit, and the Active switch that hides a sponsor
 * from the nomination form without touching existing sponsorships. Self-contained, registered as the
 * `jbp-esp-sponsor-list` viewModel.
 */
export const EspSponsorsCard: FC = () => {
  const t = useSafetyTranslation()
  const { data: sponsors, isFetching } = useGetEspSponsorListQuery()
  const [saveSponsor] = useSaveEspSponsorMutation()
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<IEspSponsorOption | 'new' | null>(null)

  const allSponsors = useMemo(() => sponsors ?? [], [sponsors])
  const query = search.trim().toLowerCase()
  const rows = useMemo(
    () =>
      query
        ? allSponsors.filter((sponsor) =>
            `${sponsor.Name} ${sponsor.Title ?? ''}`.toLowerCase().includes(query)
          )
        : allSponsors,
    [allSponsors, query]
  )
  const activeCount = useMemo(() => allSponsors.filter(isEspSponsorActive).length, [allSponsors])

  const columns = useMemo<IColumn<IEspSponsorOption>[]>(
    () => [
      {
        field: 'Name',
        title: KEYS.columnName,
        disableSort: true,
        hideSettings: true,
        titleRender: () => <Translation text={KEYS.columnName} />,
        renderFunction: (sponsor) => sponsor.Name,
      },
      {
        field: 'Title',
        title: KEYS.columnTitle,
        disableSort: true,
        hideSettings: true,
        titleRender: () => <Translation text={KEYS.columnTitle} />,
        renderFunction: (sponsor) => valueOrDash(sponsor.Title),
      },
      {
        field: 'ExecutiveLevel',
        title: KEYS.columnLevel,
        disableSort: true,
        hideSettings: true,
        width: 80,
        titleRender: () => <Translation text={KEYS.columnLevel} />,
        renderFunction: (sponsor) => valueOrDash(sponsor.ExecutiveLevel),
      },
      {
        field: 'Location',
        title: KEYS.columnRegion,
        disableSort: true,
        hideSettings: true,
        width: 90,
        titleRender: () => <Translation text={KEYS.columnRegion} />,
        renderFunction: (sponsor) => valueOrDash(sponsor.Location),
      },
      {
        field: 'IsActive',
        title: '',
        disableSort: true,
        hideSettings: true,
        width: 100,
        titleRender: () => <span />,
        renderFunction: (sponsor) => (
          <span className={styles.actions}>
            <Button
              variant="text"
              color="grey"
              iconButton="kbase kbase-edit"
              title={t(KEYS.edit)}
              onClick={() => setEditing(sponsor)}
            />
            <span aria-label={t(KEYS.fieldActive)}>
              <Switch
                size="sm"
                checked={isEspSponsorActive(sponsor)}
                onChange={(checked: boolean) =>
                  saveSponsor({
                    entryId: sponsor.EntryId,
                    input: { ...toEspSponsorInput(sponsor), IsActive: checked },
                  })
                }
              />
            </span>
          </span>
        ),
      },
    ],
    [saveSponsor, t]
  )

  const total = String(allSponsors.length)

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <span className={styles.pageCaption}>
          <Translation text={KEYS.pageCaption} span />
        </span>
        <span className={styles.pageTitle}>
          <Translation text={KEYS.pageTitle} span />
        </span>
      </div>

      <Card
        hoverable={false}
        className={styles.root}
        bodyClassName={styles.body}
        header={
          <CockpitCardHeader
            title={<Translation text={KEYS.title} />}
            description={
              <span className={styles.figures}>
                {query ? (
                  <Translation
                    text={KEYS.countShown}
                    replaceParams={[
                      { key: '{shown}', value: `<strong>${rows.length}</strong>` },
                      { key: '{total}', value: total },
                    ]}
                    span
                  />
                ) : (
                  <Translation
                    text={KEYS.countActive}
                    replaceParams={[
                      { key: '{active}', value: `<strong>${activeCount}</strong>` },
                      { key: '{total}', value: total },
                    ]}
                    span
                  />
                )}
              </span>
            }
            actions={
              <>
                <Input
                  classes={{ root: styles.search }}
                  value={search}
                  onChange={setSearch}
                  startContent={<i className="kbase kbase-search" />}
                  placeholder={t(KEYS.search)}
                />
                <CockpitIconButton
                  showLabel
                  icon="kbase kbase-add"
                  label={t(KEYS.add)}
                  onClick={() => setEditing('new')}
                />
              </>
            }
          />
        }
      >
        <LoaderOverlay isVisible={isFetching} />

        {rows.length ? (
          <Table<IEspSponsorOption>
            items={rows}
            keyField="EntryId"
            header
            columns={columns}
            // The table pages by 20 rows by default; the directory is read up to 1000 rows.
            pageSize={1000}
          />
        ) : (
          <div className={styles.empty}>
            <Translation text={allSponsors.length ? KEYS.emptySearch : KEYS.empty} span />
          </div>
        )}

        <EspSponsorDialog
          sponsor={editing === 'new' ? null : editing}
          show={editing !== null}
          onClose={() => setEditing(null)}
        />
      </Card>
    </div>
  )
}

EspSponsorsCard.displayName = 'EspSponsorsCard'
